import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NGOHome = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    openListings: 12,
    pickupsToday: 3,
    deliveriesWeek: 18,
    beneficiariesMTD: 450
  });
  
  // Unified Listings (all meals from workflow)
  const [allListings, setAllListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [filters, setFilters] = useState({
    status: 'All',
    minPortions: ''
  });
  
  // OTP verification states
  const [otpInputs, setOtpInputs] = useState({});
  const [verifyingOtp, setVerifyingOtp] = useState(null);
  
  // Profile checklist
  const [profileProgress, setProfileProgress] = useState({
    orgDetails: true,
    serviceAreas: false,
    storageCapacity: false,
    deliveryWindows: true,
    complianceDoc: false
  });

  // Profile management states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [activeProfileTab, setActiveProfileTab] = useState('info');
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    logo: null,
    logoPreview: null
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);

  useEffect(() => {
    checkAuthentication();
    loadDashboardData();
  }, []);

  const checkAuthentication = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.role !== 'ngo') {
        toast.error('Access denied. NGO role required.');
        navigate('/auth');
        return;
      }
      
      setUser(response.data);
    } catch (error) {
      toast.error('Please login to access NGO dashboard');
      navigate('/auth');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      // Load workflow listings (unified source)
      const workflowResponse = await axios.get(`${API}/workflow/ngo/listings`, {
        withCredentials: true
      });
      setAllListings(workflowResponse.data);
      setFilteredListings(workflowResponse.data);
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load meal listings');
    }
  };

  // Removed mock data - now using real workflow API data

  // Filter listings
  useEffect(() => {
    let filtered = [...allListings];
    
    if (filters.status !== 'All') {
      filtered = filtered.filter(l => l.listing_status === filters.status);
    }
    
    if (filters.minPortions) {
      filtered = filtered.filter(l => l.portions >= parseInt(filters.minPortions));
    }
    
    setFilteredListings(filtered);
  }, [filters, allListings]);

  const getFreshnessStatus = (listing) => {
    const now = new Date();
    const preparedAt = new Date(listing.prepared_at);
    const freshTill = new Date(listing.fresh_till);
    
    // Singapore rules
    if (listing.city === 'Singapore') {
      const fourHoursAfterPrep = new Date(preparedAt.getTime() + 4 * 60 * 60 * 1000);
      const oneHourBeforeFresh = new Date(freshTill.getTime() - 60 * 60 * 1000);
      
      const cutoffTime = Math.min(fourHoursAfterPrep.getTime(), oneHourBeforeFresh.getTime());
      const riskTime = cutoffTime - (60 * 60 * 1000); // 1 hour before cutoff
      
      if (now.getTime() > cutoffTime) {
        return { status: 'Expired', color: 'bg-red-100 text-red-800' };
      } else if (now.getTime() > riskTime) {
        return { status: 'Risk', color: 'bg-yellow-100 text-yellow-800' };
      } else {
        return { status: 'OK', color: 'bg-green-100 text-green-800' };
      }
    }
    
    // General rules for other cities
    if (now > freshTill) {
      return { status: 'Expired', color: 'bg-red-100 text-red-800' };
    } else if (now.getTime() > (freshTill.getTime() - 60 * 60 * 1000)) {
      return { status: 'Risk', color: 'bg-yellow-100 text-yellow-800' };
    } else {
      return { status: 'OK', color: 'bg-green-100 text-green-800' };
    }
  };

  const validateClaimTime = (listing, pickupTime) => {
    if (listing.city !== 'Singapore') return true;
    
    const pickup = new Date(pickupTime);
    const preparedAt = new Date(listing.prepared_at);
    const freshTill = new Date(listing.fresh_till);
    
    const fourHoursAfterPrep = new Date(preparedAt.getTime() + 4 * 60 * 60 * 1000);
    const oneHourBeforeFresh = new Date(freshTill.getTime() - 60 * 60 * 1000);
    
    return pickup <= fourHoursAfterPrep || pickup <= oneHourBeforeFresh;
  };

  // Removed unused claim functions - NGOs now have read-only access

  const getProgressPercentage = () => {
    const completed = Object.values(profileProgress).filter(Boolean).length;
    return Math.round((completed / Object.keys(profileProgress).length) * 100);
  };

  // Profile management functions
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        country: user.country || '',
        logo: null,
        logoPreview: user.logo || null
      });
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);

    try {
      const formData = new FormData();
      formData.append('name', profileData.name);
      formData.append('email', profileData.email);
      formData.append('phone', profileData.phone);
      formData.append('address', profileData.address);
      formData.append('city', profileData.city);
      formData.append('country', profileData.country);
      
      if (profileData.logo) {
        formData.append('logo', profileData.logo);
      }

      const response = await axios.put(`${API}/auth/profile`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Profile update error:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setProfileLoading(true);

    try {
      await axios.put(`${API}/auth/password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        withCredentials: true
      });

      toast.success('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setActiveProfileTab('info');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Current password is incorrect');
      } else {
        toast.error('Failed to update password');
      }
      console.error('Password update error:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfilePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Only PNG and JPG images are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/ngo/profile/photo/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProfilePhotoPreview(`${BACKEND_URL}${response.data.photo_url}`);
      
      // Update user object
      const updatedUser = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(updatedUser.data);
      
      toast.success('Profile photo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    }
  };

  const handleProfilePhotoRemove = async () => {
    try {
      await axios.delete(`${API}/ngo/profile/photo/remove`, {
        withCredentials: true
      });
      setProfilePhotoPreview(null);
      
      // Update user object
      const updatedUser = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(updatedUser.data);
      
      toast.success('Profile photo removed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleOpenProfileModal = () => {
    setProfileData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: user?.address || '',
      city: user?.city || '',
      country: user?.country || ''
    });
    
    if (user?.profile_picture) {
      setProfilePhotoPreview(`${BACKEND_URL}${user.profile_picture}`);
    }
    
    setShowProfileModal(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData(prev => ({
          ...prev,
          logo: file,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/auth');
    } catch (error) {
      toast.error('Failed to logout');
      console.error('Logout error:', error);
    }
  };

  // NGO Meal Claim Functions
  const handleNGOClaim = async (listingId) => {
    try {
      const response = await axios.post(
        `${API}/workflow/listings/${listingId}/ngo-claim`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message || 'Meal claimed successfully!');
      loadDashboardData(); // Refresh listings
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to claim meal';
      toast.error(errorMsg);
      console.error('Claim error:', error);
    }
  };

  const handleNGOUnclaim = async (listingId) => {
    if (!window.confirm('Are you sure you want to unclaim this meal? It will become available for other NGOs.')) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/workflow/listings/${listingId}/ngo-unclaim`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message || 'Meal unclaimed successfully');
      loadDashboardData(); // Refresh listings
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to unclaim meal';
      toast.error(errorMsg);
      console.error('Unclaim error:', error);
    }
  };

  // OTP Verification Functions
  const handleOtpInputChange = (listingId, value) => {
    setOtpInputs(prev => ({ ...prev, [listingId]: value }));
  };

  const handleVerifyDeliveryOtp = async (listingId) => {
    const otp = otpInputs[listingId];
    if (!otp || otp.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    setVerifyingOtp(listingId);
    try {
      const response = await axios.post(
        `${API}/workflow/listings/${listingId}/verify-delivery-otp`,
        { otp },
        { withCredentials: true }
      );
      toast.success(response.data.message || 'Delivery verified successfully!');
      setOtpInputs(prev => ({ ...prev, [listingId]: '' }));
      loadDashboardData(); // Refresh listings
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to verify OTP';
      toast.error(errorMsg);
    } finally {
      setVerifyingOtp(null);
    }
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm('Are you sure you want to delete your profile? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/auth/profile`, { withCredentials: true });
      toast.success('Profile deleted successfully');
      navigate('/auth');
    } catch (error) {
      toast.error('Failed to delete profile');
      console.error('Profile deletion error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading NGO dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="py-8 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Welcome, <span className="text-green-600">{user?.name}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-2">
                Claim meals, schedule pickups, and track deliveries.
              </p>
              <div className="flex items-center space-x-4">
                <Badge variant="secondary" className="capitalize">
                  {user?.role} • {user?.city}, {user?.country}
                </Badge>
              </div>
            </div>
            <div>
              <Button
                onClick={handleOpenProfileModal}
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                👤 Profile & Settings
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Top Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">{stats.openListings}</div>
              <div className="text-sm text-gray-600">Open Listings Nearby</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">{stats.pickupsToday}</div>
              <div className="text-sm text-gray-600">Pickups Today</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">{stats.deliveriesWeek}</div>
              <div className="text-sm text-gray-600">Deliveries This Week</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">{stats.beneficiariesMTD}</div>
              <div className="text-sm text-gray-600">Beneficiaries Served (MTD)</div>
            </CardContent>
          </Card>
        </div>

        {/* Unified Meal Listings View */}
        <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>All Meal Listings in {user?.city}</CardTitle>
                <CardDescription>Monitor meal listings and their progress in real-time</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="All">All Status</option>
                    <option value="OPEN">Open (Available to Claim)</option>
                    <option value="CLAIMED_BY_NGO">Claimed by NGO (Awaiting Volunteer)</option>
                    <option value="CLAIMED_BY_VOLUNTEER">Volunteer Assigned</option>
                    <option value="BEING_PICKED">Being Picked</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                  <Input
                    type="number"
                    placeholder="Min Portions"
                    value={filters.minPortions}
                    onChange={(e) => setFilters(prev => ({ ...prev, minPortions: e.target.value }))}
                  />
                </div>

                {filteredListings.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No meal listings available</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredListings.map((listing) => (
                      <Card key={listing.id} className={`border-2 ${listing.claimed_by_current_ngo ? 'border-green-400 bg-green-50' : ''}`}>
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {listing.meal_name}
                              </h3>
                              <p className="text-sm text-gray-600">
                                📍 {listing.restaurant_name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {listing.portions} portions • {listing.pickup_point}
                              </p>
                            </div>
                            <Badge className={
                              listing.listing_status === 'OPEN' ? 'bg-green-100 text-green-800' :
                              listing.listing_status === 'CLAIMED_BY_NGO' ? 'bg-orange-100 text-orange-800' :
                              listing.listing_status === 'CLAIMED_BY_VOLUNTEER' ? 'bg-blue-100 text-blue-800' :
                              listing.listing_status === 'BEING_PICKED' ? 'bg-yellow-100 text-yellow-800' :
                              listing.listing_status === 'DELIVERED' ? 'bg-purple-100 text-purple-800' :
                              listing.listing_status === 'COMPLETED' ? 'bg-gray-100 text-gray-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {listing.listing_status === 'CLAIMED_BY_NGO' && listing.claimed_by_current_ngo 
                                ? 'CLAIMED BY YOU' 
                                : listing.listing_status.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                            <div>
                              <span className="font-medium">Prepared:</span>{' '}
                              {new Date(listing.prepared_at).toLocaleString()}
                            </div>
                            <div>
                              <span className="font-medium">Fresh Till:</span>{' '}
                              {new Date(listing.fresh_till).toLocaleString()}
                            </div>
                            {listing.notes && (
                              <div className="col-span-2">
                                <span className="font-medium">Notes:</span> {listing.notes}
                              </div>
                            )}
                          </div>

                          {/* NGO Claim Info - when claimed by another NGO */}
                          {listing.listing_status === 'CLAIMED_BY_NGO' && listing.claiming_ngo && !listing.claimed_by_current_ngo && (
                            <div className="mt-3 p-3 bg-orange-50 rounded border border-orange-200">
                              <p className="text-sm font-medium text-orange-900">Claimed by another NGO:</p>
                              <p className="text-sm text-gray-700">{listing.claiming_ngo.name}</p>
                            </div>
                          )}

                          {/* NGO Claim Actions */}
                          {listing.can_claim && (
                            <div className="mt-3 flex gap-2">
                              <Button
                                onClick={() => handleNGOClaim(listing.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                🤝 Claim This Meal
                              </Button>
                            </div>
                          )}

                          {/* Unclaim button when claimed by current NGO */}
                          {listing.claimed_by_current_ngo && listing.listing_status === 'CLAIMED_BY_NGO' && (
                            <div className="mt-3 flex gap-2">
                              <div className="flex-1 p-2 bg-green-100 rounded text-sm text-green-800">
                                ✅ You claimed this meal. Waiting for a volunteer to deliver.
                              </div>
                              <Button
                                onClick={() => handleNGOUnclaim(listing.id)}
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                              >
                                Cancel Claim
                              </Button>
                            </div>
                          )}

                          {/* Volunteer Information */}
                          {listing.volunteer && (
                            <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                              <p className="text-sm font-medium text-blue-900 mb-1">🚚 Volunteer Assigned:</p>
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Name:</span> {listing.volunteer.name}
                                <br />
                                <span className="font-medium">Phone:</span> {listing.volunteer.phone}
                                <br />
                                <span className="font-medium">Email:</span> {listing.volunteer.email}
                                {listing.assignment_status && (
                                  <>
                                    <br />
                                    <span className="font-medium">Task Status:</span> {listing.assignment_status}
                                  </>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Delivery OTP Verification Section */}
                          {listing.needs_delivery_verification && listing.claimed_by_current_ngo && (
                            <div className="mt-4 p-4 bg-purple-100 rounded-lg border-2 border-purple-300">
                              <p className="text-sm font-bold text-purple-900 mb-3">
                                🚚 Volunteer has arrived! Verify delivery OTP:
                              </p>
                              <div className="flex gap-3 items-center">
                                <Input
                                  type="text"
                                  maxLength={4}
                                  placeholder="Enter 4-digit OTP"
                                  value={otpInputs[listing.id] || ''}
                                  onChange={(e) => handleOtpInputChange(listing.id, e.target.value.replace(/\D/g, ''))}
                                  className="w-32 text-center text-lg font-mono"
                                />
                                <Button
                                  onClick={() => handleVerifyDeliveryOtp(listing.id)}
                                  disabled={verifyingOtp === listing.id}
                                  className="bg-purple-600 hover:bg-purple-700"
                                >
                                  {verifyingOtp === listing.id ? 'Verifying...' : 'Verify OTP'}
                                </Button>
                              </div>
                              <p className="text-xs text-purple-700 mt-2">
                                Ask the volunteer for the OTP and enter it above to confirm delivery.
                              </p>
                            </div>
                          )}

                          {/* Pickup Status */}
                          {listing.pickup_verified && !listing.delivery_verified && listing.claimed_by_current_ngo && !listing.needs_delivery_verification && (
                            <div className="mt-3 p-2 bg-yellow-100 rounded text-sm text-yellow-700">
                              🚗 Meal picked up. Volunteer is on the way to you!
                            </div>
                          )}

                          {/* Delivery Complete Badge */}
                          {listing.delivery_verified && listing.claimed_by_current_ngo && (
                            <div className="mt-3 p-2 bg-green-100 rounded text-sm text-green-700">
                              🎉 Delivery complete! Thank you for helping reduce food waste.
                            </div>
                          )}

                          {listing.listing_status === 'OPEN' && (
                            <div className="mt-3 p-2 bg-green-50 rounded text-sm text-green-700">
                              ℹ️ This meal is available - claim it to have a volunteer deliver it to you
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
        </div>

      </div>

      {/* Profile Management Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Profile & Settings</h2>
              <button
                onClick={() => setShowProfileModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <Tabs value={activeProfileTab} onValueChange={setActiveProfileTab}>
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="info">Basic Info</TabsTrigger>
                  <TabsTrigger value="photo">Profile Photo</TabsTrigger>
                  <TabsTrigger value="security">Security</TabsTrigger>
                </TabsList>

                {/* Basic Info Tab */}
                <TabsContent value="info">
                  <form onSubmit={handleProfileUpdate} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization Name
                      </label>
                      <Input
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        placeholder="Your organization name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        placeholder="contact@organization.org"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <Input
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        placeholder="+1234567890"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <Input
                        value={profileData.address}
                        onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                        placeholder="Street address"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City
                        </label>
                        <Input
                          value={profileData.city}
                          onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                          placeholder="City"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country
                        </label>
                        <Input
                          value={profileData.country}
                          onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                          placeholder="Country"
                          required
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={profileLoading}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {profileLoading ? 'Updating...' : 'Update Profile'}
                    </Button>
                  </form>
                </TabsContent>

                {/* Profile Photo Tab */}
                <TabsContent value="photo">
                  <div className="space-y-6">
                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <div className="w-40 h-40 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center border-4 border-gray-300">
                          {profilePhotoPreview ? (
                            <img
                              src={profilePhotoPreview}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <svg
                              className="w-20 h-20 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <label htmlFor="ngo-photo-upload">
                          <Button
                            as="span"
                            className="bg-green-600 hover:bg-green-700 cursor-pointer"
                          >
                            Upload Photo
                          </Button>
                          <input
                            id="ngo-photo-upload"
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleProfilePhotoUpload}
                            className="hidden"
                          />
                        </label>

                        {profilePhotoPreview && (
                          <Button
                            variant="outline"
                            onClick={handleProfilePhotoRemove}
                            className="text-red-600 hover:text-red-700"
                          >
                            Remove Photo
                          </Button>
                        )}
                      </div>

                      <p className="text-sm text-gray-500 text-center">
                        PNG or JPG (max 5MB). Recommended: 400x400px
                      </p>
                    </div>
                  </div>
                </TabsContent>

                {/* Security Tab */}
                <TabsContent value="security">
                  <form onSubmit={handlePasswordUpdate} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password
                      </label>
                      <Input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, currentPassword: e.target.value })
                        }
                        placeholder="Enter current password"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <Input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, newPassword: e.target.value })
                        }
                        placeholder="Enter new password (min 6 characters)"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <Input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                        }
                        placeholder="Confirm new password"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={profileLoading}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {profileLoading ? 'Updating...' : 'Update Password'}
                    </Button>

                    <div className="mt-6 pt-6 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to logout?')) {
                            axios
                              .post(`${API}/auth/logout`, {}, { withCredentials: true })
                              .then(() => {
                                toast.success('Logged out successfully');
                                navigate('/auth');
                              })
                              .catch(() => toast.error('Logout failed'));
                          }
                        }}
                        className="w-full"
                      >
                        🚪 Logout
                      </Button>
                    </div>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NGOHome;